import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useMe } from '@shared/hooks/use-me';
import { useMyTransactions } from '@shared/hooks/use-transactions';
import { fullName } from '@/lib/format';

export function HomePage() {
  const { data: me } = useMe();
  const firstName = fullName(me?.firstName ?? null, null).split(' ')[0];
  const { data: transactionsPage, isLoading } = useMyTransactions({ pageSize: 100 });
  const items = transactionsPage?.items ?? [];

  // Compute metrics
  const initiatedCount = items.filter((t) =>
    ['INITIATED', 'INVOICE_CREATED'].includes(t.status),
  ).length;

  const progressCount = items.filter((t) =>
    [
      'PAID',
      'ASSIGNED',
      'ASSIGNED_REVOKED',
      'IN_PROGRESS',
      'REVIEW',
      'REVISION_REQUESTED',
      'SENT_FOR_APPROVAL',
      'APPROVED',
    ].includes(t.status),
  ).length;

  const completedCount = items.filter((t) =>
    ['COMPLETE', 'FINAL_DOC_READY'].includes(t.status),
  ).length;

  const cancelledCount = items.filter((t) => ['CANCELLED'].includes(t.status)).length;

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      {/* Welcome Card */}
      <section className="rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-50/50 via-white to-white p-6 sm:p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-2xl">
          Hello, {firstName}! <span className="align-middle"></span>
        </h1>
        <p className="mt-1.5 text-base text-gray-600 max-w-2xl leading-relaxed sm:text-sm">
          Welcome to your workspace dashboard. Track your compliance filings, access uploaded
          documents, and chat with your assigned service associates.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 sm:gap-3">
          <Link to="/client/services" className="flex-1 sm:flex-none">
            <Button
              block
              className="sm:w-auto font-semibold text-base sm:text-sm whitespace-nowrap hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150"
            >
              Browse Services
            </Button>
          </Link>
          <Link to="/client/chat" className="flex-1 sm:flex-none">
            <Button
              block
              variant="outline"
              className="sm:w-auto font-semibold text-base sm:text-sm whitespace-nowrap hover:-translate-y-0.5 active:translate-y-0 transition-all duration-150"
            >
              Open my chats
            </Button>
          </Link>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="mt-8 sm:mt-10">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3.5 px-1 sm:text-xs">
          Filing Overview
        </h2>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-28 animate-pulse rounded-2xl border border-gray-200 bg-white"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {/* Initiated Card */}
            <div className="flex flex-col justify-between rounded-2xl p-4 sm:p-5 bg-white border border-brand-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-brand-700 sm:text-[10px] md:text-xs">
                  Initiated
                </span>
                <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg sm:rounded-xl bg-brand-50 text-brand-700">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </span>
              </div>
              <div className="mt-3 flex items-baseline gap-1.5 flex-wrap sm:flex-nowrap">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                  {initiatedCount}
                </span>
                <span className="text-xs text-gray-500 font-medium sm:text-[10px] md:text-xs">
                  initiated
                </span>
              </div>
            </div>

            {/* In Progress Card */}
            <div className="flex flex-col justify-between rounded-2xl p-4 sm:p-5 bg-white border border-brand-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-brand-700 sm:text-[10px] md:text-xs">
                  In Progress
                </span>
                <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg sm:rounded-xl bg-brand-50 text-brand-700">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699-2.7a14.978 14.978 0 01-2.699-2.7"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3 flex items-baseline gap-1.5 flex-wrap sm:flex-nowrap">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                  {progressCount}
                </span>
                <span className="text-xs text-gray-500 font-medium sm:text-[10px] md:text-xs">
                  in progress
                </span>
              </div>
            </div>

            {/* Completed Card */}
            <div className="flex flex-col justify-between rounded-2xl p-4 sm:p-5 bg-white border border-emerald-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-emerald-700 sm:text-[10px] md:text-xs">
                  Completed
                </span>
                <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg sm:rounded-xl bg-emerald-50 text-emerald-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3 flex items-baseline gap-1.5 flex-wrap sm:flex-nowrap">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                  {completedCount}
                </span>
                <span className="text-xs text-gray-500 font-medium sm:text-[10px] md:text-xs">
                  completed
                </span>
              </div>
            </div>

            {/* Cancelled Card */}
            <div className="flex flex-col justify-between rounded-2xl p-4 sm:p-5 bg-white border border-brand-600">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600 sm:text-[10px] md:text-xs">
                  Cancelled
                </span>
                <span className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-lg sm:rounded-xl bg-slate-50 text-slate-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-3 flex items-baseline gap-1.5 flex-wrap sm:flex-nowrap">
                <span className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                  {cancelledCount}
                </span>
                <span className="text-xs text-gray-500 font-medium sm:text-[10px] md:text-xs">
                  cancelled
                </span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Quick Access Grid */}
      <section className="mt-10 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4 px-1 sm:text-xs">
          Quick Access
        </h2>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Services Catalog */}
          <Link
            to="/client/services"
            className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-brand-350 hover:shadow-md hover:-translate-y-0.5 group"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 group-hover:bg-brand-700 group-hover:text-white transition-all duration-200 shadow-xs">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v5.25c0 .414.336.75.75.75z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-gray-900 group-hover:text-brand-900 sm:text-sm">
                Services Catalog
              </h3>
              <p className="mt-1 text-sm text-gray-500 leading-relaxed sm:text-xs">
                Explore corporate, tax, compliance and drafting services.
              </p>
            </div>
          </Link>

          {/* Active Chats */}
          <Link
            to="/client/chat"
            className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-brand-350 hover:shadow-md hover:-translate-y-0.5 group"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 group-hover:bg-brand-700 group-hover:text-white transition-all duration-200 shadow-xs">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-gray-900 group-hover:text-brand-900 sm:text-sm">
                Service Chats
              </h3>
              <p className="mt-1 text-sm text-gray-500 leading-relaxed sm:text-xs">
                Communicate directly with your assigned associate.
              </p>
            </div>
          </Link>

          {/* EMI Calculator */}
          <Link
            to="/client/emi-calculator"
            className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-brand-350 hover:shadow-md hover:-translate-y-0.5 group"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 group-hover:bg-brand-700 group-hover:text-white transition-all duration-200 shadow-xs">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 15.75V18m-3-2.25V18m-3-2.25V18m9-6V9M15 9V6.75M12 9V6.75M9 9V6.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-gray-900 group-hover:text-brand-900 sm:text-sm">
                EMI Calculator
              </h3>
              <p className="mt-1 text-sm text-gray-500 leading-relaxed sm:text-xs">
                Calculate monthly loan installments, interest & repayment schedules.
              </p>
            </div>
          </Link>

          {/* Profile & KYC */}
          <Link
            to="/client/profile"
            className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-brand-350 hover:shadow-md hover:-translate-y-0.5 group"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 group-hover:bg-brand-700 group-hover:text-white transition-all duration-200 shadow-xs">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-gray-900 group-hover:text-brand-900 sm:text-sm">
                KYC & Profile
              </h3>
              <p className="mt-1 text-sm text-gray-500 leading-relaxed sm:text-xs">
                Update details and identity verification.
              </p>
            </div>
          </Link>
        </div>
      </section>

      <p className="mt-10 text-center text-sm text-gray-400 sm:mt-12 select-none sm:text-xs">
        All your details stay encrypted and safe.
      </p>
    </div>
  );
}
